import React, { useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { money } from "@/lib/api";

const RED = "#E51937";
const BLACK = "#0A0A0A";
const PIE = ["#E51937", "#0A0A0A", "#F59E0B", "#71717A"];

const Panel = ({ title, children }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
    <h3 className="font-heading font-bold mb-4">{title}</h3>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return null;

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl mb-1">Analytics</h1>
      <p className="text-sm text-muted-foreground mb-5">{a.total_orders} total orders · {money(a.discount_used)} in discounts used.</p>
      <div className="grid grid-cols-2 gap-4 mb-5 max-w-md">
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm" data-testid="analytics-visitors">
          <p className="text-xs text-muted-foreground">Customers who visited</p>
          <p className="font-heading font-extrabold text-3xl text-metros-red">{a.visitors_unique ?? 0}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm" data-testid="analytics-visits">
          <p className="text-xs text-muted-foreground">Total website visits</p>
          <p className="font-heading font-extrabold text-3xl">{a.visits_total ?? 0}</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Website visits (last 14 days)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={a.visits_series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="visits" stroke={RED} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Revenue (last 14 days)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={a.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Line type="monotone" dataKey="revenue" stroke={RED} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Orders (last 14 days)">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="orders" fill={BLACK} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Best sellers">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.best_sellers} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
              <Tooltip />
              <Bar dataKey="qty" fill={RED} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Peak ordering times">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.peak_hours}>
              <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={1} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="orders" fill={BLACK} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Delivery vs Collection">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={a.channel_split} dataKey="value" nameKey="name" outerRadius={80} label>
                {a.channel_split.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="New vs Returning">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={a.customer_split} dataKey="value" nameKey="name" outerRadius={80} label>
                {a.customer_split.map((_, i) => <Cell key={i} fill={PIE[(i + 1) % PIE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
